import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Container, Row, Col, ProgressBar } from 'react-bootstrap'
import { listSections } from '../actions/courseActions'
import Loader from '../components/Loader'
import Message from '../components/Message'

// Icon map per section order — expand as you add more sections
const SECTION_ICONS = {
    1: 'fa-chart-line',
    2: 'fa-scale-balanced',
    3: 'fa-building-columns',
    4: 'fa-coins',
    5: 'fa-shield-halved',
    6: 'fa-file-contract',
}

function SectionCard({ section, onClick }) {
    const progress = section.user_progress
    const isLocked    = !progress || !progress.is_unlocked
    const isCompleted = progress?.is_completed
    const icon = SECTION_ICONS[section.order] || 'fa-book-open'

    const progressPercent = isCompleted ? 100 : isLocked ? 0 : 50

    return (
        <div
            className={`section-card ${isLocked ? 'locked' : ''} mb-4`}
            onClick={() => !isLocked && onClick(section._id)}
            role={isLocked ? 'presentation' : 'button'}
            aria-label={`Section ${section.order}: ${section.title}`}
        >
            <div className="section-card-header">
                <i className={`fa-solid ${icon} section-icon`}></i>
                <div>
                    <div style={{ fontSize: '0.72rem', opacity: 0.7, marginBottom: 2 }}>
                        SECTION {section.order}
                    </div>
                    <h5>{section.title}</h5>
                </div>
                {section.exam_weight && (
                    <span className="badge-weight">{section.exam_weight}</span>
                )}
            </div>
            <div className="section-card-body">
                <p>{section.description}</p>

                {/* Status row */}
                <div className="d-flex align-items-center justify-content-between mb-2">
                    {isLocked ? (
                        <span style={{ fontSize: '0.82rem', color: 'var(--locked)' }}>
                            <i className="fa-solid fa-lock me-1"></i>Complete previous section to unlock
                        </span>
                    ) : isCompleted ? (
                        <span style={{ fontSize: '0.82rem', color: 'var(--success)', fontWeight: 600 }}>
                            <i className="fa-solid fa-circle-check me-1"></i>Completed
                        </span>
                    ) : (
                        <span style={{ fontSize: '0.82rem', color: 'var(--gold)', fontWeight: 600 }}>
                            <i className="fa-solid fa-circle-play me-1"></i>In Progress
                        </span>
                    )}
                    {section.has_quiz && !isLocked && (
                        <span style={{ fontSize: '0.78rem', color: 'var(--muted)' }}>
                            <i className="fa-solid fa-pen-to-square me-1"></i>Quiz available
                        </span>
                    )}
                </div>

                {/* Progress bar */}
                <div className="progress-label">
                    <span>Progress</span>
                    <span>{progressPercent}%</span>
                </div>
                <div className="progress">
                    <div
                        className={`progress-bar ${isCompleted ? 'completed' : ''}`}
                        style={{ width: `${progressPercent}%` }}
                        role="progressbar"
                        aria-valuenow={progressPercent}
                        aria-valuemin="0"
                        aria-valuemax="100"
                    />
                </div>
            </div>
        </div>
    )
}

function CourseScreen() {
    const dispatch  = useDispatch()
    const navigate  = useNavigate()
    const userLogin = useSelector(state => state.userLogin)
    const { userInfo } = userLogin
    const { loading, error, sections } = useSelector(state => state.sectionsList)

    useEffect(() => {
        if (!userInfo) {
            navigate('/login')
            return
        }
        dispatch(listSections())
    }, [dispatch, navigate, userInfo])

    const completedCount = sections.filter(s => s.user_progress?.is_completed).length
    const totalCount     = sections.length
    const overallPct     = totalCount ? Math.round((completedCount / totalCount) * 100) : 0

    return (
        <Container>
            {/* Page header */}
            <div className="hero-banner">
                <h1><i className="fa-solid fa-book-open-reader me-2"></i>Full Course</h1>
                <p>
                    Complete each section in order. A quiz unlocks the next section.<br />
                    <strong style={{ color: 'var(--gold-lt)' }}>
                        {completedCount} of {totalCount} sections completed
                    </strong>
                </p>
                {totalCount > 0 && (
                    <div style={{ marginTop: '1rem', maxWidth: 400 }}>
                        <div className="progress-label" style={{ color: 'rgba(255,255,255,0.6)' }}>
                            <span>Overall Progress</span><span>{overallPct}%</span>
                        </div>
                        <div className="progress">
                            <div
                                className={`progress-bar ${overallPct === 100 ? 'completed' : ''}`}
                                style={{ width: `${overallPct}%` }}
                            />
                        </div>
                    </div>
                )}
            </div>

            {loading ? (
                <Loader />
            ) : error ? (
                <Message variant="danger">{error}</Message>
            ) : (
                <Row>
                    {sections.map(section => (
                        <Col key={section._id} md={6} xl={4}>
                            <SectionCard
                                section={section}
                                onClick={(id) => navigate(`/section/${id}`)}
                            />
                        </Col>
                    ))}
                </Row>
            )}
        </Container>
    )
}

export default CourseScreen
